// components/EssayResultsDisplay.jsx
import React, { useState } from 'react';
import { Lightbulb, Package, CheckCircle2, ChevronRight, MessageSquare, Heart, Zap, Sparkles, Target, Eye, FileText } from 'lucide-react';
import EssayTopicDevelopment from './EssayTopicDevelopment';

// Interview-specific theming - MINIMAL color, typography & layout emphasis
const INTERVIEW_THEMES = {
  hidden_competencies: {
    hero: {
      icon: Zap,
      label: 'What Makes You Stand Out',
      accentColor: 'text-blue-600', // Only for icon
      borderAccent: 'border-blue-600'
    },
    tone: {
      heading: 'Skills & Problem-Solving',
      nextStepsIntro: 'Strategic steps to develop these topics',
      topicsSubtitle: 'Ranked by potential impact. Start with #1.'
    }
  },
  
  character_identity: {
    hero: {
      icon: Heart,
      label: 'Who You Really Are',
      accentColor: 'text-violet-600',
      borderAccent: 'border-violet-600'
    },
    tone: {
      heading: 'Character & Values',
      nextStepsIntro: 'Deepen these stories through reflection and honest exploration',
      topicsSubtitle: 'Each topic reveals a different facet of your character'
    }
  },

  snapshots: {
    hero: {
      icon: Eye, // or Camera - import from lucide-react
      label: 'What You Notice',
      accentColor: 'text-amber-600',
      borderAccent: 'border-amber-600'
    },
    tone: {
      heading: 'Memory & Observation',
      nextStepsIntro: 'Develop these moments into vivid, specific narratives',
      topicsSubtitle: 'Stories from what you remember and return to'
    }
  },

  quick_brainstorm: {
    hero: {
      icon: Sparkles,
      label: 'Fresh Angles Discovered',
      accentColor: 'text-emerald-600',
      borderAccent: 'border-emerald-600'
    },
    tone: {
      heading: 'Rapid Ideation',
      nextStepsIntro: 'Jump in and start exploring these angles',
      topicsSubtitle: 'Multiple angles to explore—choose what resonates'
    }
  },
  
  common_topics: {
    hero: {
      icon: Target,
      label: 'Your Fresh Take',
      accentColor: 'text-amber-600',
      borderAccent: 'border-amber-600'
    },
    tone: {
      heading: 'Traditional Prompts, New Angles',
      nextStepsIntro: 'Tactical approach to crafting essays from these topics',
      topicsSubtitle: 'Fresh perspectives on familiar prompts—ranked by distinctiveness'
    }
  },
  negotiation_persuasion: {
    hero: {
      icon: MessageSquare,
      label: 'How You Navigate Influence',
      accentColor: 'text-purple-600',
      borderAccent: 'border-purple-600'
    },
    tone: {
      heading: 'Persuasion & Conflict Resolution',
      nextStepsIntro: 'Develop these tactical insights into essays that reveal your approach',
      topicsSubtitle: 'Each topic reveals a different aspect of your negotiation signature'
    }
  },
sentimental_objects: {
    hero: {
      icon: Package,
      label: 'Stories in Your Objects',
      accentColor: 'text-indigo-600',
      borderAccent: 'border-indigo-600'
    },
    tone: {
      heading: 'Object-Based Essay Topics',
      nextStepsIntro: 'Develop these topics by expanding on specific moments and details',
      topicsSubtitle: 'Ranked by essay potential - start with highest ranked'
    }
  
  }
};

export default function EssayResultsDisplay({ insights, interviewType }) {
  const [developingTopic, setDevelopingTopic] = useState(null);

  if (!insights) return null;

  const theme = INTERVIEW_THEMES[interviewType] || INTERVIEW_THEMES.hidden_competencies;
  const HeroIcon = theme.hero.icon;

  const handleDevelopTopic = (topic) => {
    setDevelopingTopic(topic);
  };

  const handleBeginDrafting = (topic) => {
    console.log('Begin drafting with topic:', topic);
    // TODO: Navigate to essay drafting interface
  };

  const handleDevelopmentComplete = (data) => {
    console.log('Development complete:', data);
    setDevelopingTopic(null);
    // TODO: Optionally refresh essay idea data from database
  };

  return (
    <div className="space-y-8">
      {/* Hero Section - Minimal with dot accent */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-2 h-2 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')}`}></div>
          <h2 className="text-base font-bold text-slate-900">{theme.hero.label}</h2>
        </div>
        <div className="pl-4 border-l-2 border-slate-900">
          <p className="text-sm text-slate-700 leading-relaxed">
            {insights.magneticHook}
          </p>
        </div>
      </div>

      {/* Surprise Insight */}
      {insights.surpriseInsight && (
        <div className="bg-stone-50 border border-stone-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')} mt-2 flex-shrink-0`}></div>
            <div>
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">Surprise Insight</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                {insights.surpriseInsight}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Essay Topics - Minimal Grid Structure */}
      {insights.essayTopics && insights.essayTopics.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-slate-900"></div>
            <h3 className="text-sm font-bold text-slate-900">Essay Topics</h3>
          </div>
          <p className="text-xs text-slate-600 mb-4 pl-4">
            {theme.tone.topicsSubtitle}
          </p>

          <div className="space-y-4">
            {insights.essayTopics.map((topic, index) => (
              <div 
                key={index}
                className="border-2 border-slate-900 rounded-xl bg-white overflow-hidden"
              >
                {/* Topic Header */}
                <div className="bg-slate-900 px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')}`}></div>
                    <span className="text-xs font-bold text-white">
                      TOPIC {index + 1}
                      {index === 0 && <span className="ml-2 text-gold-400">● START HERE</span>}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  {/* Title */}
                  <h4 className="text-sm font-bold text-slate-900 leading-tight mb-3">
                    {topic.title}
                  </h4>

                  {/* Story */}
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">
                    {topic.story}
                  </p>

                  {/* Essay Angle */}
                  {topic.essayAngle && (
                    <div className={`bg-stone-50 rounded-lg p-3 border-l-2 ${theme.hero.borderAccent} mb-3`}>
                      <p className="text-xs text-slate-700 leading-relaxed italic">
                        <span className="font-bold not-italic">Strategic Angle:</span> {topic.essayAngle}
                      </p>
                    </div>
                  )}

                  {/* Prompt Count */}
                  {topic.potentialPrompts && topic.potentialPrompts.length > 0 && (
                    <div className="text-xs text-slate-500 mb-3">
                      Works for {topic.potentialPrompts.length} prompt{topic.potentialPrompts.length > 1 ? 's' : ''}
                    </div>
                  )}

                  {/* Expandable Details */}
                  <details className="mt-3 group/details">
                    <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-slate-900 hover:text-slate-600 list-none flex items-center gap-2 transition-colors">
                      <ChevronRight className="w-3 h-3 transition-transform group-open/details:rotate-90" />
                      <span>Full Breakdown</span>
                    </summary>
                    
                    <div className="mt-3 pt-3 border-t border-stone-200 space-y-4">
                      {/* Story Structure */}
                      {topic.narrativeArc && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-1 bg-slate-900 rounded-full"></div>
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-900">
                              How to Structure This
                            </div>
                          </div>
                          <p className="text-xs text-slate-600 leading-relaxed pl-3">
                            {topic.narrativeArc}
                          </p>
                        </div>
                      )}

                      {/* Two Column Grid */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {topic.whyItWorks && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-1 h-1 bg-slate-900 rounded-full"></div>
                              <div className="text-xs font-bold uppercase tracking-wider text-slate-900">
                                Why It Works
                              </div>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed pl-3">
                              {topic.whyItWorks}
                            </p>
                          </div>
                        )}

                        {topic.differentiator && (
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-1 h-1 bg-slate-900 rounded-full"></div>
                              <div className="text-xs font-bold uppercase tracking-wider text-slate-900">
                                What Makes It Yours
                              </div>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed pl-3">
                              {topic.differentiator}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Source Quote */}
                      {topic.sourceQuote && (
                        <div className="bg-stone-50 border-l-2 border-slate-900 p-3">
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2">
                            From Your Interview
                          </div>
                          <p className="text-xs text-slate-600 italic leading-relaxed">
                            "{topic.sourceQuote}"
                          </p>
                        </div>
                      )}

                      {/* Prompts */}
                      {topic.potentialPrompts && topic.potentialPrompts.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-1 bg-slate-900 rounded-full"></div>
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-900">
                              Works For These Prompts
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 pl-3">
                            {topic.potentialPrompts.map((prompt, pIndex) => (
                              <span 
                                key={pIndex}
                                className="text-xs px-2 py-1 border border-stone-300 bg-white text-slate-700 rounded"
                              >
                                {prompt}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </details>

                  {/* Action Buttons */}
                  <div className="mt-4 pt-4 border-t border-stone-200 flex gap-2">
                    <button
                      onClick={() => handleDevelopTopic(topic)}
                      className="flex-1 px-3 py-2 bg-slate-900 text-white text-xs font-bold rounded hover:bg-slate-700 transition-colors uppercase tracking-wide"
                    >
                      Develop Topic
                    </button>
                    <button
                      onClick={() => handleBeginDrafting(topic)}
                      className="flex-1 px-3 py-2 border-2 border-slate-900 text-slate-900 text-xs font-bold rounded hover:bg-slate-900 hover:text-white transition-colors uppercase tracking-wide"
                    >
                      Begin Draft
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Development Modal */}
      {developingTopic && (
        <EssayTopicDevelopment
          essayIdea={developingTopic}
          onComplete={handleDevelopmentComplete}
          onClose={() => setDevelopingTopic(null)}
        />
      )}

      {/* Next Steps - Minimal with border */}
      {insights.nextSteps && insights.nextSteps.length > 0 && (
        <div className="border-2 border-slate-900 rounded-xl overflow-hidden">
          <div className="bg-slate-900 px-5 py-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')}`}></div>
              <h3 className="text-xs font-bold text-white uppercase tracking-wide">
                Your Next Steps
              </h3>
            </div>
          </div>
          <div className="bg-white p-5">
            <p className="text-xs text-slate-600 mb-4">
              {theme.tone.nextStepsIntro}
            </p>
            <div className="space-y-3">
              {insights.nextSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-slate-900 rounded-full flex-shrink-0 mt-1.5"></div>
                    <span className="text-xs font-bold text-slate-900">{index + 1}.</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed flex-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Thematic Connections - Minimal with dot */}
      {insights.thematicConnections && (
        <div className="border border-stone-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <div className={`w-2 h-2 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')} mt-1 flex-shrink-0`}></div>
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 mb-2">
                The Connecting Thread
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                {insights.thematicConnections}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Strengths - Collapsible */}
      {insights.hiddenStrengths && insights.hiddenStrengths.length > 0 && (
        <details className="group border-2 border-slate-900 rounded-xl overflow-hidden">
          <summary className="cursor-pointer bg-slate-900 hover:bg-slate-800 transition-colors px-5 py-3 list-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${theme.hero.accentColor.replace('text-', 'bg-')}`}></div>
                <div>
                  <h4 className="text-xs font-bold text-white uppercase tracking-wide">
                    Hidden Strengths
                  </h4>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {insights.hiddenStrengths.length} distinctive qualities
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-white transition-transform group-open:rotate-90" />
            </div>
          </summary>
          
          <div className="bg-white p-5 border-t border-stone-200">
            <div className="space-y-2">
              {insights.hiddenStrengths.map((strength, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-slate-900 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p className="text-xs text-slate-600 leading-relaxed">{strength}</p>
                </div>
              ))}
            </div>
          </div>
        </details>
      )}
    </div>
  );
}
